import { DataTestIdEnum } from '@medprev-web-app/datatestid';
import { EntityRatingCommentCount, Professional, Rating } from '@medprev-web-app/api-graphql-client';

import { EntityRating } from '@/app/(main)/(pages)/_components/entities/entity-rating-sumary.component';
import { formatToTitleCase } from '@/utils/formatters/format-to-title-case';
import { EntityActionHeader } from '@/app/(main)/(pages)/_components/entities/entity-action-header.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';
import { formatSpecialties } from '@/utils/professional/professional-license.util';
import { EntityAvatar } from '@/components/ui/avatar/entity-avatar';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

interface ProfessionalHeaderProps {
  professional: Professional;
  commentsCount?: EntityRatingCommentCount | null;
  shouldAddLightbox?: boolean;
  hasSchedulesSection?: boolean;
}

const resolveProfessionalName = (professional: Professional): string => {
  const { name, honorific } = professional;

  if (!honorific) {
    return name ?? '';
  }

  return formatToTitleCase(`${honorific} ${name}`);
};

export function ProfessionalHeader({
  professional,
  commentsCount,
  shouldAddLightbox = false,
  hasSchedulesSection = true,
}: ProfessionalHeaderProps) {
  const formatLicense = (): string | undefined => {
    const { licenses } = professional;

    const license = licenses.length ? licenses[0] : undefined;

    if (!license) {
      return;
    }

    return `${license.certificationBodyInitials}/${license.licenseState} - ${license.licenseRecord}`;
  };

  return (
    <>
      <div
        className="flex flex-col md:flex-row items-center md:items-start gap-6"
        data-testid={DataTestIdEnum.PROFESSIONAL_PROFILE_HEADER}
      >
        <EntityAvatar
          src={professional.avatar as string}
          size="4xl"
          alt={`Foto de perfil de ${professional.name}`}
          shouldAddLightbox={shouldAddLightbox}
          type={EntityTypeEnum.PROFESSIONAL}
          dataTestId={professional.avatar
            ? DataTestIdEnum.PROFESSIONAL_PROFILE_AVATAR
            : DataTestIdEnum.PROFESSIONAL_PROFILE_AVATAR_DEFAULT}
          data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_CLICK_AVATAR}
        />

        <div>
          <div className="max-w-xs sm:max-w-[400px] m-auto md:m-0">
            <Typography
              variant={TypographyVariant.MODAL_TITLE}
              as="h1"
              className="text-center md:text-left break-words text-wrap hyphens-auto overflow-hidden"
            >
              {resolveProfessionalName(professional)}
            </Typography>
          </div>

          <ol className="flex flex-col items-center md:items-start mt-2">
            {formatSpecialties(professional).map((specialty) => (
              <li className="text-gray-700 text-sm text-center md:text-start" key={specialty}>
                {specialty}
              </li>
            ))}

            <li className="text-gray-700 text-sm">
              <span className="flex items-center gap-1" data-testid={DataTestIdEnum.PROFESSIONAL_LICENSE_HEADER}>
                {formatLicense()}
              </span>
            </li>
          </ol>
        </div>
      </div>
      <div className="mt-5">
        <EntityRating
          rating={professional.rating as Rating}
          commentsCount={commentsCount}
          data-analytics-event={AnalyticsEnum.PROFESSIONAL_PROFILE_CLICK_REVIEWS_WITH_COMMENT}
        />
      </div>

      <div className="mt-8">
        <EntityActionHeader
          entity={professional}
          entityType={EntityTypeEnum.PROFESSIONAL}
          hasSchedulesSection={hasSchedulesSection}
        />
      </div>
    </>
  );
}
